import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { GoogleMap, Marker, Circle, InfoWindow } from '@react-google-maps/api';
import { useGoogleMapsLoader } from '../../utils/googleMapsLoader';

const mapContainerStyle = {
  width: '100%',
  height: '100%',
  minHeight: '300px',
  borderRadius: '8px'
};

// Default center is Sandton, Johannesburg, South Africa
const defaultCenter = { lat: -26.107567, lng: 28.056702 };

const options = {
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: true,
  streetViewControl: false,
  fullscreenControl: true,
};

const ClientMap = ({ assetLocation, geofenceRadius = 500 }) => {
  const [map, setMap] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  
  // Use the shared Google Maps loader
  const { isLoaded, loadError, showFallback } = useGoogleMapsLoader();

  const onLoad = useCallback(function callback(map) {
    mapRef.current = map;
    setMap(map);
  }, []);

  const onUnmount = useCallback(function callback() {
    mapRef.current = null;
    setMap(null);
  }, []);
  
  // Handle resize events to prevent ResizeObserver errors
  useEffect(() => {
    // Only run this effect if Google Maps is loaded
    if (!isLoaded) return;
    
    const resizeHandler = () => {
      // Trigger a resize event on the map after a small delay
      // This helps the map adjust to its container size changes
      setTimeout(() => {
        // Check if map reference is available
        if (mapRef.current && window.google) {
          try {
            window.google.maps.event.trigger(mapRef.current, 'resize');
            
            // If we have a location, recenter the map
            if (assetLocation && mapRef.current.setCenter) {
              mapRef.current.setCenter({
                lat: assetLocation.lat,
                lng: assetLocation.lng
              });
            }
          } catch (error) {
            console.error('Error resizing map:', error);
          }
        }
      }, 100);
    };
    
    window.addEventListener('resize', resizeHandler);
    
    // Initial resize trigger - only if map is available
    if (mapRef.current) {
      resizeHandler();
    }
    
    return () => {
      window.removeEventListener('resize', resizeHandler);
    };
  }, [assetLocation, map, isLoaded]);

  if (loadError || showFallback) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100%',
          bgcolor: 'grey.100',
          borderRadius: '8px',
          p: 3
        }}
      >
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          Map Unavailable
        </Typography>
        <Typography color="text.secondary" align="center">
          The map could not be loaded. Asset tracking is still active in the background.
        </Typography>
      </Box>
    );
  }

  if (!isLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Use default Johannesburg location if no asset location is provided
  const center = assetLocation ? {
    lat: assetLocation.lat,
    lng: assetLocation.lng
  } : defaultCenter;

  return (
    <Box sx={{ height: '100%', flexGrow: 1 }} ref={mapContainerRef}>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={center}
        zoom={14}
        options={options}
        onLoad={onLoad}
        onUnmount={onUnmount}
      >
        <Marker 
          position={center} 
          onClick={() => setInfoOpen(true)}
        />
        {infoOpen && (
          <InfoWindow
            position={center}
            onCloseClick={() => setInfoOpen(false)}
          >
            <div>
              <Typography variant="body2">Your asset is here</Typography>
            </div>
          </InfoWindow>
        )}
        <Circle
          center={center}
          radius={geofenceRadius}
          options={{
            strokeColor: '#3f51b5',
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: '#3f51b5',
            fillOpacity: 0.1,
          }}
        />
      </GoogleMap>
    </Box>
  );
};

export default ClientMap;
